import React from 'react';
import { ArrowUp } from 'lucide-react';
import AdSpace from '../components/AdSpace';
import { useSetMeta } from '../hooks/useSetMeta';

import termsData from '../src/content/terms.json';
import privacyData from '../src/content/privacy.json';
import aboutData from '../src/content/about.json';
import contactData from '../src/content/contact.json';

const ScrollToTopButton = () => (
    <div className="flex justify-center mt-8">
        <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg shadow-purple-600/30 transition-all hover:scale-105 hover:shadow-purple-600/50"
        >
            <ArrowUp size={18} />
            <span>Back to Top</span>
        </button>
    </div>
);

export const TermsOfService = () => {
    useSetMeta({
      title: 'Terms of Service | ZodiAge',
      description: 'Read ZodiAge terms of service. Legal agreement governing your use of our astrology and zodiac tools.',
      ogImage: 'https://zodiage.com/logo.png'
    });
    return (
    <div className="max-w-4xl mx-auto space-y-12 pb-12">
        <div className="seo-content" dangerouslySetInnerHTML={{ __html: termsData.html }} />
        <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
        <ScrollToTopButton />
    </div>
  );
};

export const PrivacyPolicy = () => {
    useSetMeta({
      title: 'Privacy Policy | ZodiAge',
      description: 'ZodiAge privacy policy. Learn how we protect your personal data and use information on our astrology platform.',
      ogImage: 'https://zodiage.com/logo.png'
    });
    return (
    <div className="max-w-4xl mx-auto space-y-12 pb-12">
        <div className="seo-content" dangerouslySetInnerHTML={{ __html: privacyData.html }} />
        <AdSpace type="multiplex" slot="1588142002" className="mt-12" />
        <ScrollToTopButton />
    </div>
  );
};

export const About = () => {
    useSetMeta({
      title: 'About ZodiAge | Astrology & Zodiac Tools',
      description: 'Learn about ZodiAge - our mission to provide accurate zodiac calculations, tarot readings, and astrological insights.',
      ogImage: 'https://zodiage.com/logo.png'
    });
    return (
    <div className="max-w-4xl mx-auto space-y-12 pb-12">
        <div className="seo-content" dangerouslySetInnerHTML={{ __html: aboutData.html }} />
        <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
        <ScrollToTopButton />
    </div>
  );
};

export const Contact = () => {
    const [submitted, setSubmitted] = React.useState(false);
    useSetMeta({
      title: 'Contact Us | ZodiAge',
      description: 'Get in touch with the ZodiAge team. Have questions about our astrology tools or features? Contact us here.',
      ogImage: 'https://zodiage.com/logo.png'
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const name = (form.elements.namedItem('name') as HTMLInputElement).value;
        const email = (form.elements.namedItem('email') as HTMLInputElement).value;
        const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
        const subject = encodeURIComponent(`ZodiAge Inquiry from ${name}`);
        const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`);
        window.location.href = `mailto:support@zodiage.com?subject=${subject}&body=${body}`;
        setSubmitted(true);
    };

    return (
    <div className="max-w-4xl mx-auto space-y-12 pb-12">
        <div className="seo-content" dangerouslySetInnerHTML={{ __html: contactData.html }} />

        {/* Contact Form */}
        <div className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 mt-8">
            <h2 className="text-2xl font-bold text-white mb-4">Send us a Message</h2>
            {submitted ? (
                <div className="text-center py-8">
                    <p className="text-green-400 text-lg font-semibold mb-2">Thank you for reaching out!</p>
                    <p className="text-gray-300">Your email client has been opened with your message. We typically respond within 48–72 hours.</p>
                </div>
            ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="form-group text-left">
                    <label className="text-gray-300 block mb-2">Name</label>
                    <input name="name" type="text" className="w-full bg-cosmic-900/50 border border-cosmic-700 rounded-lg p-3 text-white" placeholder="Your name" required />
                </div>
                <div className="form-group text-left">
                    <label className="text-gray-300 block mb-2">Email Address</label>
                    <input name="email" type="email" className="w-full bg-cosmic-900/50 border border-cosmic-700 rounded-lg p-3 text-white" placeholder="your@email.com" required />
                </div>
                <div className="form-group text-left">
                    <label className="text-gray-300 block mb-2">Message</label>
                    <textarea name="message" className="w-full bg-cosmic-900/50 border border-cosmic-700 rounded-lg p-3 text-white min-h-[150px]" placeholder="How can we help?" required></textarea>
                </div>
                <button type="submit" className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold py-3 px-8 rounded-lg w-full hover:opacity-90 transition-opacity">
                    Send Message
                </button>
            </form>
            )}
        </div>

        <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
        <ScrollToTopButton />
    </div>
    );
};

export const EditorialPolicy = () => (
    <div className="max-w-4xl mx-auto space-y-10">
        <header className="text-left space-y-4">
            <h1 className="text-4xl font-display font-bold text-white">Editorial Policy & Methodology</h1>
            <p className="text-gray-400 text-sm">Last updated: February 2026</p>
            <p className="text-lg text-gray-300 leading-relaxed">
                This document explains how ZodiAge creates content, conducts research, ensures accuracy, and handles corrections.
                It also details the technical methodology behind our calculations and features.
            </p>
        </header>

        {/* PART 1: EDITORIAL POLICY */}
        <div className="border-b border-gray-700 pb-4">
            <h2 className="text-xl font-bold text-primary uppercase tracking-wide">Part 1: Editorial Standards</h2>
        </div>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Content Philosophy</h2>
            <p className="text-gray-300 leading-relaxed">
                ZodiAge creates content that bridges scholarly understanding of astrological traditions with accessible,
                engaging presentation for general audiences. We respect the cultural and historical significance of symbolic
                systems while maintaining clarity about their nature as frameworks for reflection rather than predictive science.
            </p>
            <p className="text-gray-300 leading-relaxed">
                Every piece of content is designed to inform, engage, and encourage thoughtful self-reflection. We prioritize
                educational value over sensationalism and accuracy over entertainment.
            </p>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Content Creation Process</h2>
            <div className="bg-cosmic-800/30 p-5 rounded-xl border border-cosmic-700/50">
                <ol className="space-y-3 text-gray-300">
                    <li className="flex gap-3">
                        <span className="text-primary font-bold">1.</span>
                        <span><strong className="text-white">Topic Research:</strong> We consult traditional sources, academic literature, and authoritative references before writing about any subject.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="text-primary font-bold">2.</span>
                        <span><strong className="text-white">Drafting:</strong> Content is written with a focus on clarity, accuracy, and educational value. Complex concepts are explained without oversimplification.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="text-primary font-bold">3.</span>
                        <span><strong className="text-white">Editorial Review:</strong> All content is reviewed for factual accuracy, appropriate tone, and consistency with our content philosophy.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="text-primary font-bold">4.</span>
                        <span><strong className="text-white">Publication & Updates:</strong> Content is published with clear dates and updated when new information warrants revision.</span>
                    </li>
                </ol>
            </div>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Fact-Checking Standards</h2>
            <p className="text-gray-300 leading-relaxed">
                We apply different verification standards to different types of content:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Astronomical Data:</strong> Cross-referenced with authoritative sources (NASA, IAU, USNO).</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Calendar Calculations:</strong> Verified against standard algorithms and tested across edge cases.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Historical Claims:</strong> Sourced from academic or encyclopedic references.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Astrological Interpretations:</strong> Based on traditional sources, clearly labeled as symbolic content.</li>
            </ul>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Update & Revision Policy</h2>
            <p className="text-gray-300 leading-relaxed">
                We review and update content in the following circumstances:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span>When new authoritative data becomes available</li>
                <li className="flex gap-2"><span className="text-primary">•</span>When errors are identified and confirmed</li>
                <li className="flex gap-2"><span className="text-primary">•</span>When user feedback highlights unclear explanations</li>
                <li className="flex gap-2"><span className="text-primary">•</span>When technical improvements affect feature behavior</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
                Substantial updates are noted with revised "Last updated" dates. For transparency, we do not silently
                alter previously published content in ways that materially change its meaning.
            </p>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Corrections Policy</h2>
            <p className="text-gray-300 leading-relaxed">
                We take accuracy seriously. If you identify a factual error, unclear explanation, or misleading content:
            </p>
            <ol className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary font-bold">1.</span>Email us at <a href="mailto:info@zodiage.com" className="text-primary hover:underline">info@zodiage.com</a> with "Correction Request" in the subject line.</li>
                <li className="flex gap-2"><span className="text-primary font-bold">2.</span>Include the specific page URL and the content in question.</li>
                <li className="flex gap-2"><span className="text-primary font-bold">3.</span>Provide supporting sources if available.</li>
            </ol>
            <p className="text-gray-300 leading-relaxed mt-3">
                We review all correction requests within 5 business days. Confirmed errors are corrected promptly,
                and significant corrections are acknowledged transparently.
            </p>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Editorial Independence</h2>
            <p className="text-gray-300 leading-relaxed">
                Our editorial content is created independently of advertisers and commercial interests:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">No Paid Content:</strong> We do not accept payment for editorial coverage or placement.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Ad Separation:</strong> Advertisements are visually distinct from editorial content.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">No Affiliate Influence:</strong> Content is not modified based on affiliate relationships.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Honest Presentation:</strong> We disclose AI-generated content and data sources.</li>
            </ul>
        </section>

        {/* PART 2: METHODOLOGY */}
        <div className="border-b border-gray-700 pb-4 mt-12">
            <h2 className="text-xl font-bold text-primary uppercase tracking-wide">Part 2: Technical Methodology</h2>
        </div>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Age Calculation Algorithm</h2>
            <p className="text-gray-300 leading-relaxed">
                Our age calculator uses precise date arithmetic that correctly handles complex calendar edge cases:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Leap Years:</strong> Correctly identifies leap years (divisible by 4, except century years not divisible by 400).</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Month Lengths:</strong> Respects actual month lengths (28-31 days) including February edge cases.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Timezone Support:</strong> When birth time is provided, timezone offset is factored into calculations.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Real-Time Display:</strong> The counter updates every second using browser time APIs.</li>
            </ul>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Western Zodiac Methodology</h2>
            <p className="text-gray-300 leading-relaxed">
                We use the <strong className="text-white">Tropical Zodiac</strong> system, the standard in Western astrology:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Sun Signs:</strong> Determined by the Sun's ecliptic longitude relative to the vernal equinox (0° Aries = March equinox).</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Moon Signs:</strong> Calculated using simplified lunar position algorithms based on birth date.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Rising Signs:</strong> Requires birth time; calculated from the ecliptic degree rising on the eastern horizon.</li>
            </ul>
            <div className="bg-cosmic-800/30 p-4 rounded-xl border border-cosmic-700/50 mt-4">
                <h4 className="font-bold text-white mb-2">Tropical vs. Sidereal</h4>
                <p className="text-sm text-gray-300">
                    The Tropical zodiac is season-based (aligned to equinoxes/solstices), while the Sidereal zodiac is
                    constellation-based. Due to precession, they differ by about 24°. ZodiAge uses Tropical, consistent
                    with mainstream Western astrological practice.
                </p>
            </div>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Chinese Zodiac Methodology</h2>
            <p className="text-gray-300 leading-relaxed">
                Our Chinese zodiac calculator follows traditional 12-year animal cycles and 5-element associations:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span>Animal assignment based on lunar new year boundaries (not January 1st)</li>
                <li className="flex gap-2"><span className="text-primary">•</span>Element (Wood, Fire, Earth, Metal, Water) derived from 10-year heavenly stem cycle</li>
                <li className="flex gap-2"><span className="text-primary">•</span>Yin/Yang polarity based on animal position in cycle</li>
            </ul>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">AI Content Generation</h2>
            <p className="text-gray-300 leading-relaxed">
                Certain personalized features use Google Gemini AI. Our approach:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Prompt Design:</strong> We craft prompts that encourage reflective, non-prescriptive responses.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Context Provided:</strong> Only zodiac sign, mood selection, and question topic—no personal identifiers.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Fresh Generation:</strong> Each response is generated anew, not pulled from templates.</li>
                <li className="flex gap-2"><span className="text-primary">•</span><strong className="text-white">Disclosure:</strong> AI-generated content is clearly indicated in the interface.</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
                For legal terms regarding AI content, see <a href="/terms" className="text-primary hover:underline">Terms of Service §6</a>.
                For data handling details, see <a href="/privacy" className="text-primary hover:underline">Privacy Policy §4</a>.
            </p>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Data Sources</h2>
            <p className="text-gray-300 leading-relaxed">We reference these authoritative sources for factual data:</p>
            <div className="bg-cosmic-800/30 p-4 rounded-xl border border-cosmic-700/50 mt-3">
                <ul className="text-sm text-gray-300 space-y-2">
                    <li><strong className="text-white">NASA Solar System Exploration</strong> — Planetary positions, astronomical events</li>
                    <li><strong className="text-white">International Astronomical Union (IAU)</strong> — Constellation definitions</li>
                    <li><strong className="text-white">U.S. Naval Observatory</strong> — Time calculations, calendar algorithms</li>
                    <li><strong className="text-white">World Health Organization</strong> — Life expectancy statistics</li>
                    <li><strong className="text-white">Encyclopædia Britannica</strong> — Historical context for symbolic traditions</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">
                    Astrological interpretations are based on traditional symbolic systems and are not claims endorsed by scientific organizations.
                </p>
            </div>
        </section>

        <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Known Limitations</h2>
            <p className="text-gray-300 leading-relaxed">
                For transparency, we acknowledge these limitations in our methodology:
            </p>
            <ul className="text-gray-300 space-y-2 mt-3">
                <li className="flex gap-2"><span className="text-primary">•</span>Rising sign calculations use simplified algorithms; professional astrologers may use more precise ephemeris data.</li>
                <li className="flex gap-2"><span className="text-primary">•</span>Chinese New Year boundary dates use approximation rules; exact dates vary year to year.</li>
                <li className="flex gap-2"><span className="text-primary">•</span>Life expectancy statistics are global averages and do not reflect individual health factors.</li>
                <li className="flex gap-2"><span className="text-primary">•</span>AI-generated content varies in quality and relevance; responses are not curated.</li>
            </ul>
        </section>

        <section className="space-y-4 border-t border-gray-800 pt-8">
            <p className="text-gray-400 text-sm text-center">
                Questions about our editorial standards or methodology? Contact us at
                <a href="mailto:info@zodiage.com" className="text-primary hover:underline ml-1">info@zodiage.com</a>.
            </p>
        </section>

        <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
        <ScrollToTopButton />
    </div>
);
